import Loader from '@components/framework/loader/Loader';
import DateInput from '@components/system/DateInput/DateInput';
import StyledOutlinedInput from '@components/system/FormInput/StyledOutlinedInput';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  makeStyles
} from '@material-ui/core';
import {manualVerificationValidation} from '@shared/modules/eligibility-verification/EligibilityVerification';
import {ManualVerificationFormType} from '@shared/modules/eligibility-verification/EligibilityVerificationFromType';
import {manuallyVerify} from '@shared/services/InsuranceEligibilityServices';
import {useSnackbar} from 'notistack';
import React, {ComponentProps} from 'react';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {APP_PRIMARY_COLOR} from 'src/style/variable';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';

interface ManualVerificationModalProps {
  isOpen: boolean;
  handleClose: () => void;
  appointmentId: string | number;
  clinicGroupId: string | number;
  clinicId: string | number;
  onSuccess: () => void;
}

const DEFAULT_VERIFY_INSURANCE_FIELD_VALUES: ManualVerificationFormType = {
  insurance_name: '',
  subscriber_group_number: '',
  subscriber_member_id: '',
  plan_name: '',
  plan_start_date: null,
  plan_end_date: null,
  plan_number: '',
  policy_number: ''
};
type FormFieldType<T> = {
  key: T;
  label: string;
  placeholder: string;
  defaultValue: any;
  required?: boolean;
  type?: ComponentProps<typeof StyledOutlinedInput>['type'];
};
type FormField<T extends Record<string, any>> = FormFieldType<keyof T>[];
const manualVerificationInsuranceFromFields: FormField<ManualVerificationFormType> = [
  {
    key: 'insurance_name',
    label:
      'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.INSURANCE_NAME',
    defaultValue: DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['insurance_name'],
    placeholder:
      'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.INSURANCE_NAME_PLACEHOLDER',
    required: true,
    type: 'text'
  },
  {
    key: 'subscriber_group_number',
    label:
      'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.SUBSCRIBER_GROUP_NUMBER',
    defaultValue:
      DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['subscriber_group_number'],
    placeholder:
      'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.SUBSCRIBER_GROUP_NUMBER_PLACEHOLDER',
    required: true,
    type: 'text'
  },
  {
    key: 'subscriber_member_id',
    label:
      'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.SUBSCRIBER_MEMBER_ID',
    defaultValue: DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['subscriber_member_id'],
    placeholder:
      'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.SUBSCRIBER_MEMBER_ID_PLACEHOLDER',
    required: true,
    type: 'text'
  },
  {
    key: 'plan_name',
    label: 'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.PLAN_NAME',
    defaultValue: DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['plan_name'],
    placeholder:
      'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.PLAN_NAME_PLACEHOLDER',
    required: true,
    type: 'text'
  },
  {
    key: 'plan_number',
    label: 'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.PLAN_NUMBER',
    defaultValue: DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['plan_number'],
    placeholder:
      'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.PLAN_NUMBER_PLACEHOLDER',
    required: true,
    type: 'text'
  },
  {
    key: 'plan_start_date',
    label:
      'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.PLAN_START_DATE',
    defaultValue: DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['plan_start_date'],
    placeholder:
      'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.PLAN_START_DATE_PLACEHOLDER',
    required: true,
    type: 'date'
  },
  {
    key: 'plan_end_date',
    label:
      'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.PLAN_END_DATE',
    defaultValue: DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['plan_end_date'],
    placeholder:
      'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.PLAN_END_DATE_PLACEHOLDER',
    required: true,
    type: 'date'
  },
  {
    key: 'policy_number',
    label:
      'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.POLICY_NUMBER',
    defaultValue: DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['policy_number'],
    placeholder:
      'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.POLICY_NUMBER_PLACEHOLDER',
    required: true,
    type: 'text'
  }
];

const styles = makeStyles(() => ({
  formControl: {
    width: '100%',
    marginBottom: 0
  },
  linkStyle: {
    whiteSpace: 'nowrap',
    lineHeight: '1.25rem',
    cursor: 'pointer',
    color: APP_PRIMARY_COLOR,
    textDecoration: 'underline'
  },
  dateInput: {
    marginTop: '8px',
    paddingBottom: '0px',
    marginBottom: '0px'
  }
}));

const ManualVerificationModal = ({
  isOpen,
  handleClose,
  appointmentId,
  clinicGroupId,
  clinicId,
  onSuccess
}: ManualVerificationModalProps): JSX.Element => {
  const {t} = useTranslation();
  const {control, errors, handleSubmit, watch} = useForm({
    mode: 'onChange',
    defaultValues: DEFAULT_VERIFY_INSURANCE_FIELD_VALUES,
    resolver: values => {
      const formError = manualVerificationValidation(values);
      const hasError = Object.keys(formError).length > 0;
      return {
        values: hasError ? {} : values,
        errors: hasError ? formError : {}
      };
    }
  });

  // eslint-disable-next-line camelcase
  const {
    plan_start_date: planStartDateSate,
    plan_end_date: planEndDateSate
  } = watch(['plan_start_date', 'plan_end_date']);
  // Date Input's min and max date's type should be date or undefined
  // but the value we receive from react-hook-form could be null or '', so need to convert them.
  const planStartDate = planStartDateSate ?? undefined;
  const planEndDate = planEndDateSate ?? undefined;

  const classes = styles();
  const {enqueueSnackbar} = useSnackbar();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const handleFormSubmit: SubmitHandler<ManualVerificationFormType> = async data => {
    setIsSubmitting(true);
    try {
      const formattedData = {
        ...data,
        plan_start_date: data.plan_start_date?.toString() ?? '',
        plan_end_date: data.plan_end_date?.toString() ?? ''
      };
      await manuallyVerify(
        clinicGroupId,
        clinicId,
        appointmentId,
        formattedData
      );
      onSuccess();
      handleClose();
    } catch (error: any) {
      enqueueSnackbar(
        error?.message
          ? error.message
          : t(`ELIGIBILITY_VERIFICATION.ERROR_IN_VERIFYING_MANUALLY`),
        SnackBarConfig('e')
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Dialog
      open={isOpen}
      fullWidth={true}
      maxWidth={'sm'}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}>
      <DialogTitle disableTypography>
        {t(
          'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.ADD_INSURANCE_INFORMATION'
        )}
      </DialogTitle>
      <DialogContent dividers>
        {manualVerificationInsuranceFromFields.map(formField => (
          <Controller
            key={formField.key}
            name={formField.key}
            control={control}
            render={field => (
              <FormControl
                className={classes.formControl}
                error={!!errors[formField.key]}>
                <p>
                  {t(formField.label)}
                  {formField.required && `*`}
                </p>
                {formField.type === 'date' ? (
                  <DateInput
                    // setting mid date for plan end date
                    minDate={
                      formField.key === 'plan_end_date'
                        ? planStartDate
                        : undefined
                    }
                    // setting max date for plan start date
                    maxDate={
                      formField.key === 'plan_start_date'
                        ? planEndDate
                        : undefined
                    }
                    className={classes.dateInput}
                    {...field}
                    placeholder={t(formField.placeholder)}
                  />
                ) : (
                  <StyledOutlinedInput
                    margin={'dense'}
                    {...field}
                    placeholder={t(formField.placeholder)}
                    type={formField.type ?? 'text'}
                  />
                )}
                {errors?.[formField.key]?.message && (
                  <FormHelperText>
                    {t(errors?.[formField.key]?.message as string)}
                  </FormHelperText>
                )}
              </FormControl>
            )}
            defaultValue={
              DEFAULT_VERIFY_INSURANCE_FIELD_VALUES['insurance_name']
            }
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" disabled={isSubmitting}>
          {t('ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_FORM.CANCEL')}
        </Button>
        <Button
          onClick={handleSubmit(handleFormSubmit)}
          color="primary"
          variant="contained"
          disabled={isSubmitting}>
          {!isSubmitting ? (
            t('ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.SAVE')
          ) : (
            <Loader />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualVerificationModal;
